import type { ReactNode } from 'react';
import { FaEllipsisH } from 'react-icons/fa';
import { Dropdown } from '@/components/UI';

export interface ActionMenuItem {
    label: string;
    /** One line under the label saying what the action does. */
    description?: string;
    onSelect: () => void;
    disabled?: boolean;
    tone?: 'default' | 'danger';
}

interface ActionMenuProps {
    items: ActionMenuItem[];
    /** Accessible name of the trigger, e.g. "Actions for ext-50-retest-25". */
    label: string;
    /** Trigger content; defaults to an ellipsis icon. */
    trigger?: ReactNode;
    buttonClassName?: string;
}

/** Overflow menu for secondary row and page actions, so tables keep one visible action at most. */
const ActionMenu = ({ items, label, trigger, buttonClassName = '' }: ActionMenuProps) => {
    if (items.length === 0) return null;

    return (
        <Dropdown
            closeOnItemClick
            buttonClassName={`border border-zinc-700 bg-zinc-900 px-2 py-1 text-zinc-300 hover:bg-zinc-800 ${buttonClassName}`}
            buttonContent={
                trigger ?? (
                    <>
                        <FaEllipsisH aria-hidden="true" className="h-3.5 w-3.5" />
                        <span className="sr-only">{label}</span>
                    </>
                )
            }
            hideChevron={trigger === undefined}
            menuClassName="w-64 border border-zinc-800"
            position="right"
        >
            {items.map((item) => (
                <button
                    className="w-full px-3 py-2 text-left transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={item.disabled}
                    key={item.label}
                    role="menuitem"
                    type="button"
                    onClick={item.onSelect}
                >
                    <div className={`text-sm ${item.tone === 'danger' ? 'text-red-300' : 'text-zinc-200'}`}>{item.label}</div>
                    {item.description && <div className="text-[11px] leading-snug text-zinc-500">{item.description}</div>}
                </button>
            ))}
        </Dropdown>
    );
};

export default ActionMenu;
